import apiRequest from './apiRequest';
import ServerAddress from '../utils/ServerAddress';

const uploadComplianceDocument = async (url, token, formData) => {
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        body: formData,
    });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Upload failed');
    }

    return response.json();
};

const complianceApi = {
    // Dashboard APIs
    getDashboardSummary(userId, token) {
        const url = userId 
            ? `/api/compliance/dashboard/summary?userId=${userId}` 
            : '/api/compliance/dashboard/summary';
        return apiRequest(url, 'GET', token);
    },

    getUpcomingDocuments(days = 7, token) {
        return apiRequest(`/api/compliance/dashboard/upcoming?days=${days}`, 'GET', token);
    },

    getOverdueDocuments(token) {
        return apiRequest('/api/compliance/dashboard/overdue', 'GET', token);
    },

    getRecentlyCompleted(days = 30, token) {
        return apiRequest(`/api/compliance/dashboard/recently-completed?days=${days}`, 'GET', token);
    },

    // Document APIs
    getDocumentById(id, token) {
        return apiRequest(`/api/compliance/documents/${id}`, 'GET', token);
    },

    getDocumentsByAccount(accountId, token) {
        return apiRequest(`/api/compliance/documents/account/${accountId}`, 'GET', token);
    },

    getDocumentsByAccountAndCategory(accountId, category, token) {
        return apiRequest(`/api/compliance/documents/account/${accountId}/category/${category}`, 'GET', token);
    },

    getDocumentsByAccountAndFiscalYear(accountId, fiscalYear, token) {
        return apiRequest(`/api/compliance/documents/account/${accountId}/fiscal-year/${fiscalYear}`, 'GET', token);
    },

    getDocumentsByGstin(gstin, token) {
        return apiRequest(`/api/compliance/documents/gstin/${gstin}`, 'GET', token);
    },

    updateDocumentStatus(id, data, token) {
        return apiRequest(`/api/compliance/documents/${id}/status`, 'PUT', token, data);
    },

    linkDocument(id, data, token) {
        return apiRequest(`/api/compliance/documents/${id}/link`, 'POST', token, data);
    },

    getDocumentAuditTrail(id, token) {
        return apiRequest(`/api/compliance/documents/${id}/audit-trail`, 'GET', token);
    },

    getLinkedDocuments(id, token) {
        return apiRequest(`/api/compliance/documents/${id}/linked`, 'GET', token);
    },

    deleteDocument(id, token) {
        return apiRequest(`/api/compliance/documents/${id}`, 'DELETE', token);
    },

    // Upload Document API (multipart form data)
    uploadDocument(requestData, file, token) {
        const formData = new FormData();
        formData.append('request', new Blob([JSON.stringify(requestData)], { type: 'application/json' }));
        formData.append('file', file);
        const url = `${ServerAddress}/api/compliance/documents/upload`;
        return uploadComplianceDocument(url, token, formData);
    },

    // Risk APIs
    getRiskScore(accountId, token) {
        return apiRequest(`/api/compliance/risk/account/${accountId}`, 'GET', token);
    },

    getRiskHistory(accountId, token) {
        return apiRequest(`/api/compliance/risk/account/${accountId}/history`, 'GET', token);
    },

    calculateRiskScore(accountId, fiscalYear, token) {
        return apiRequest(`/api/compliance/risk/account/${accountId}/calculate?fiscalYear=${fiscalYear}`, 'POST', token);
    },

    getRiskDashboard(token) {
        return apiRequest('/api/compliance/risk/dashboard', 'GET', token);
    },

    // Tracker APIs
    getMonthlyTracker(accountId, category, fiscalYear, token) {
        return apiRequest(`/api/compliance/tracker/monthly?accountId=${accountId}&category=${category}&fiscalYear=${fiscalYear}`, 'GET', token);
    },

    getQuarterlyTracker(accountId, category, fiscalYear, token) {
        return apiRequest(`/api/compliance/tracker/quarterly?accountId=${accountId}&category=${category}&fiscalYear=${fiscalYear}`, 'GET', token);
    },

    getAllAccountsTracker(category, fiscalYear, token) {
        return apiRequest(`/api/compliance/tracker/all-accounts?category=${category}&fiscalYear=${fiscalYear}`, 'GET', token);
    },

    getBeforeMarchDashboard(fiscalYear, token) {
        return apiRequest(`/api/compliance/tracker/before-march?fiscalYear=${fiscalYear}`, 'GET', token);
    },

    getGSTTracker(accountId, fiscalYear, token) {
        return apiRequest(`/api/compliance/tracker/gst?accountId=${accountId}&fiscalYear=${fiscalYear}`, 'GET', token);
    },

    getTDSTracker(accountId, fiscalYear, token) {
        return apiRequest(`/api/compliance/tracker/tds?accountId=${accountId}&fiscalYear=${fiscalYear}`, 'GET', token);
    },

    // Notification Settings APIs
    getAllGlobalNotificationSettings(token) {
        return apiRequest('/api/compliance/notifications/global', 'GET', token);
    },

    getGlobalNotificationSettingsByCategory(category, token) {
        return apiRequest(`/api/compliance/notifications/global/${category}`, 'GET', token);
    },

    saveGlobalNotificationSettings(data, token) {
        return apiRequest('/api/compliance/notifications/global', 'POST', token, data);
    },

    getAccountNotificationSettings(accountId, token) {
        return apiRequest(`/api/compliance/notifications/account/${accountId}`, 'GET', token);
    },

    getAccountNotificationSettingsByCategory(accountId, category, token) {
        return apiRequest(`/api/compliance/notifications/account/${accountId}/${category}`, 'GET', token);
    },

    saveAccountNotificationSettings(data, token) {
        return apiRequest('/api/compliance/notifications/account', 'POST', token, data);
    },

    deleteAccountNotificationSettings(settingsId, token) {
        return apiRequest(`/api/compliance/notifications/account/${settingsId}`, 'DELETE', token);
    },
};

export default complianceApi;
